import { useState } from "react";
import { postFileDownloadURL } from "../http/chapterApi";
import SVGDelete from "../icons/Delete.svg?react";
import { DeleteDocumentModalType } from "../types/apiTypes";
import { DocumentFile, FileType, ListItemData } from "../types/types";
import { formatDate, getIcon } from "../utils";
import styles from "./DocumentsListItem.module.css";
import Button from "./ui/Button";
import Icon from "./ui/Icon";
import Modal from "./ui/Modal";

interface Props {
  chapterId: number;
  file: DocumentFile;
  onDelete: (id: number) => void;
}

const DocumentsListItem = ({ chapterId, file, onDelete }: Props) => {
  const [modal, setModal] = useState<DeleteDocumentModalType>({
    isOpen: false,
    file: undefined,
  });
  const lastDotIndex = file.name.lastIndexOf(".");

  const dataSplit = [
    file.name.substring(0, lastDotIndex),
    file.name.substring(lastDotIndex + 1),
  ];

  const fileObject: ListItemData = {
    name: dataSplit[0] ?? "",
    extension: dataSplit[1] as FileType,
  };

  const downloadFile = async (chapterId: number, fileName: string) => {
    try {
      const downloadResponse = await postFileDownloadURL(chapterId, fileName);

      const link = document.createElement("a");
      link.href = downloadResponse.downloadUrl;
      link.download = fileName;
      link.target = "_blank";
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading file:", error);
      throw error;
    }
  };

  return (
    <tr className={styles.row}>
      <td className={styles.label}>
        <Button
          buttonProps={{
            onClick: () => {
              downloadFile(chapterId, file.name);
            },
            className: styles["btn-download-document"],
          }}
        >
          <Icon
            className={styles["file-icon"]}
            glyph={getIcon(fileObject.extension)}
          />
          <span className={styles.label}>
            {fileObject.name}
            <span className={styles.extension}>.{fileObject.extension}</span>
          </span>
        </Button>
      </td>
      <td className={styles.label}>{formatDate(new Date(file.updatedAt))}</td>
      <td className={styles.label}>
        <Button
          buttonProps={{
            className: styles["clear-button"],
            onClick: () => {
              setModal({ isOpen: !modal.isOpen, file: file });
            },
          }}
        >
          <Icon glyph={SVGDelete} className={styles["clear-icon"]} />
        </Button>
        {modal.isOpen && modal.file && (
          <DocumentModal
            data={modal.file}
            onDelete={onDelete}
            onClose={() => setModal({ isOpen: false, file: undefined })}
          />
        )}
      </td>
    </tr>
  );
};

interface DocumentModalProps {
  data: DocumentFile;
  onClose: () => void;
  onDelete: (id: number) => void;
}

const DocumentModal = ({ data, onClose, onDelete }: DocumentModalProps) => (
  <Modal onClose={onClose}>
    <div>
      <p className={styles.text}>
        Möchten Sie <strong>{data.name}</strong> wirklich löschen?
      </p>
      <div className={styles.container}>
        <Button
          buttonProps={{
            onClick: () => {
              onClose();
            },
          }}
        >
          Abbrechen
        </Button>
        <Button
          buttonProps={{
            onClick: () => {
              onClose();
              onDelete(data.id);
            },
          }}
        >
          Löschen
        </Button>
      </div>
    </div>
  </Modal>
);

export default DocumentsListItem;
